import type { XopQuote } from "@xop-any-time/shared";
import { Box, Typography } from "@mui/material";
import { useEffect, useState, type JSX } from "react";

import { PdfReader } from "../../../components/pdf/pdf-reader.js";
import { downloadXopQuotePdf as downloadRendered, fetchXopQuotePdf } from "../../../lib/documentPdf.js";

/**
 * The XOP quote, as a file — THE FILE crm-api RENDERS, and nothing drawn here.
 *
 * ─────────────────────────────────────────────────────────────────────
 * ONE GENERATOR (2026-09-17)
 * ─────────────────────────────────────────────────────────────────────
 * This page used to map the portal's `XopQuote` onto a jsPDF generator ported
 * from the command center and draw the document in the browser. Three
 * generators drew the same documents three slightly different ways, so the
 * stack now has one: crm-api's `InvoicePdfRenderer`, run on the quote's CRM
 * copy. The portal fetches those bytes (`lib/documentPdf.ts`) and paints them
 * with pdf.js onto a canvas — never a `blob:` iframe.
 *
 * The bytes are FRESH on every fetch: the reader transfers the buffer it is
 * given, so Download fetches its own rather than reusing the preview's.
 */

/** `XOP-Networks-Quote-XQ-2026-0007.pdf` — the name used only when crm-api's
 * response carries none. Anything that is not a letter, digit or dash
 * becomes a dash so the name is safe on every filesystem. */
export function xopQuotePdfFilename(quote: Pick<XopQuote, "id" | "number">): string {
  const stem = (quote.number ?? String(quote.id))
    .replace(/[^A-Za-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return `XOP-Networks-Quote-${stem}.pdf`;
}

/** Save the rendered quote under crm-api's filename. */
export function downloadXopQuotePdf(quote: Pick<XopQuote, "id" | "number">): Promise<void> {
  return downloadRendered(quote.id, xopQuotePdfFilename(quote));
}

/** The document, on screen. `null` bytes while the fetch runs; a stated
 * outcome, not a blank card, when crm-api cannot produce it. */
export function XopQuotePdfPreview({ quote }: { quote: XopQuote }): JSX.Element {
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setBytes(null);
    setFailed(false);
    fetchXopQuotePdf(quote.id)
      .then(({ bytes: fetched }) => {
        if (alive) setBytes(fetched);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
    // Refetch when the quote CHANGES (a decision re-renders the document's
    // status), not on every new object identity from a refetch.
  }, [quote.id, quote.status, quote.decidedAt]);

  return (
    <Box data-testid="xop-quote-pdf-preview" sx={{ minHeight: 120 }}>
      {bytes === null && !failed && (
        <Typography sx={{ color: "text.secondary", fontSize: 14, py: 4, textAlign: "center" }}>
          Loading the document…
        </Typography>
      )}
      {failed && (
        <Typography
          data-testid="xop-quote-pdf-unavailable"
          sx={{ color: "text.secondary", fontSize: 14, py: 4, textAlign: "center" }}
        >
          This quote’s document couldn’t be loaded. Try again in a moment, or use Download.
        </Typography>
      )}
      <PdfReader
        bytes={bytes}
        filename={xopQuotePdfFilename(quote)}
        containerTestId="xop-quote-pdf-canvas"
        fallbackTestId="xop-quote-pdf-fallback"
        fallbackText="This quote couldn’t be previewed inline. Use Download to save it."
      />
    </Box>
  );
}
